import { useEffect, useRef, useState } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const KPI_DATA = [
  { label: "Casos activos", value: "34", trend: "+12%", trendUp: true, bar: 68, color: "bg-primary-500", icon: "ri-folder-3-line" },
  { label: "Intervenciones", value: "127", trend: "+18%", trendUp: true, bar: 82, color: "bg-accent-500", icon: "ri-heart-pulse-line" },
  { label: "Cumplimiento protocolos", value: "94%", trend: "+7%", trendUp: true, bar: 94, color: "bg-secondary-500", icon: "ri-check-double-line" },
  { label: "Casos resueltos", value: "19", trend: "+5%", trendUp: true, bar: 56, color: "bg-emerald-500", icon: "ri-flag-2-line" },
];

const ALERTS = [
  { text: "3 casos críticos sin intervención en +72h", severity: "critical", icon: "ri-error-warning-line" },
  { text: "2 informes para tribunal vencen esta semana", severity: "warning", icon: "ri-file-warning-line" },
  { text: "5 derivaciones pendientes de confirmación", severity: "warning", icon: "ri-share-forward-line" },
];

const DASHBOARD_CASES = [
  { student: "Martín Soto", grade: "2° Medio B", status: "En intervención", statusColor: "bg-primary-100 text-primary-700", days: 3 },
  { student: "Emilia Rojas", grade: "8° Básico A", status: "Crítico", statusColor: "bg-red-100 text-red-700", days: 0 },
  { student: "Benjamín Cáceres", grade: "4° Medio C", status: "Seguimiento", statusColor: "bg-secondary-100 text-secondary-700", days: 12 },
];

export default function HeroSection() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.1, selector: ".hero-animate", staggerDelay: 100, revealClass: "animate-reveal" });
  const dashboardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!dashboardRef.current) return;
      const scrollY = window.scrollY;
      const rate = scrollY * 0.06;
      dashboardRef.current.style.transform = `translateY(${rate}px)`;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDemoSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState("submitting");
    const form = e.currentTarget;
    const formData = new FormData(form);
    const urlEncoded = new URLSearchParams(formData as unknown as Record<string, string>).toString();
    try {
      await fetch("https://readdy.ai/api/form/d8lfboom1jqlm5gfvsrg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlEncoded,
      });
      setFormState("success");
      setTimeout(() => { setDemoOpen(false); setFormState("idle"); }, 2500);
    } catch {
      setFormState("error");
      setTimeout(() => setFormState("idle"), 3000);
    }
  };

  return (
    <section ref={sectionRef} id="hero" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-accent-50/60 animate-hero-gradient" />

      {/* Floating orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="orb orb-primary w-[500px] h-[500px] top-[-15%] left-[-8%] animate-drift-slow" />
        <div className="orb orb-accent w-[400px] h-[400px] top-[40%] right-[-10%] animate-drift-medium" style={{ animationDelay: "2s" }} />
        <div className="orb orb-secondary w-[350px] h-[350px] bottom-[-12%] left-[20%] animate-drift-fast" style={{ animationDelay: "4s" }} />
        <div className="orb orb-primary w-[250px] h-[250px] top-[60%] left-[50%] animate-drift-slow" style={{ animationDelay: "6s" }} />
      </div>

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: "radial-gradient(circle, oklch(var(--foreground-950)) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }} />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-24 md:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* LEFT: Text content */}
          <div className="flex-1 w-full">
            <div className="hero-animate scroll-reveal flex items-center gap-2 mb-5">
              <span className="px-3 py-1.5 text-xs font-semibold tracking-wider uppercase bg-primary-100 text-primary-700 rounded-full">
                Plataforma SaaS
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-soft-glow" />
              <span className="text-xs text-foreground-400 font-medium">Gestión de Convivencia Escolar</span>
            </div>

            <h1 className="hero-animate scroll-reveal text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-[1.08] tracking-tight">
              Gestión inteligente de la convivencia escolar para{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 via-primary-500 to-accent-500">
                colegios y equipos psicosociales
              </span>
            </h1>

            <p className="hero-animate scroll-reveal text-lg md:text-xl text-foreground-500 leading-relaxed mt-5 max-w-xl">
              Centraliza casos, intervenciones, protocolos, derivaciones y reportabilidad de convivencia en una única plataforma con inteligencia artificial integrada.
            </p>

            <div className="hero-animate scroll-reveal flex flex-col sm:flex-row gap-3 mt-8">
              <button
                onClick={() => setDemoOpen(true)}
                className="group relative px-7 py-4 bg-gradient-to-r from-primary-600 to-primary-500 text-white text-sm font-bold rounded-2xl hover:from-primary-700 hover:to-primary-600 active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-glow-primary overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Solicitar demo
                  <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              </button>
              <a
                href="#convivencia-escolar"
                className="px-7 py-4 bg-white border border-background-200 text-foreground-800 text-sm font-semibold rounded-2xl hover:border-primary-200 hover:text-primary-700 hover:bg-primary-50/50 active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-soft"
              >
                <i className="ri-function-line" />
                Ver funcionalidades
              </a>
            </div>

            {/* Trust indicators */}
            <div className="hero-animate scroll-reveal flex flex-wrap items-center gap-5 mt-10 pt-8 border-t border-background-200/80">
              {[
                { value: "99.9%", label: "Disponibilidad", icon: "ri-cloud-line" },
                { value: "SOC 2", label: "Datos protegidos", icon: "ri-shield-check-line" },
                { value: "+50", label: "Colegios activos", icon: "ri-building-4-line" },
              ].map((item, i) => (
                <div key={item.label} className="flex items-center gap-2.5" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                    <i className={`${item.icon} text-foreground-400 text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground-900">{item.value}</p>
                    <p className="text-xs text-foreground-400">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Convivencia Dashboard Mockup */}
          <div ref={dashboardRef} className="flex-1 w-full max-w-[520px] animate-dashboard-float">
            <div className="relative">
              {/* Glow behind dashboard */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary-400/20 via-accent-400/10 to-secondary-400/15 blur-3xl animate-glow-pulse-blue" />

              <div className="relative glass-panel-strong rounded-3xl p-5 md:p-6 shadow-soft-lg">
                {/* Dashboard header */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider">Panel de Convivencia</p>
                    <p className="text-sm font-bold text-foreground-900 mt-0.5">Resumen institucional</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-secondary-500 animate-status-pulse" />
                    <span className="text-xs text-foreground-400 font-medium">En vivo</span>
                  </div>
                </div>

                {/* KPI Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {KPI_DATA.map((kpi, i) => (
                    <div key={kpi.label} className="widget-card" style={{ animationDelay: `${i * 150}ms` }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-7 h-7 rounded-lg ${kpi.color} flex items-center justify-center`}>
                          <i className={`${kpi.icon} text-white text-xs`} />
                        </div>
                        <span className={`text-xs font-semibold ${kpi.trendUp ? "text-secondary-600" : "text-red-500"}`}>
                          {kpi.trend}
                        </span>
                      </div>
                      <p className="text-xl font-extrabold text-foreground-950 animate-number-tick">{kpi.value}</p>
                      <p className="text-xs text-foreground-400 mt-0.5">{kpi.label}</p>
                      <div className="mt-2 h-1.5 bg-background-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${kpi.color} rounded-full animate-kpi-bar`}
                          style={{ width: `${kpi.bar}%`, animationDelay: `${i * 200}ms` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Active cases */}
                <div className="widget-card mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-folder-3-line text-primary-500 text-sm" />
                    <span className="text-xs font-bold text-foreground-800 uppercase tracking-wider">Casos activos recientes</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 text-xs font-bold">{DASHBOARD_CASES.length}</span>
                  </div>
                  <div className="space-y-2">
                    {DASHBOARD_CASES.map((c, i) => (
                      <div key={c.student} className="flex items-center gap-3 py-1.5 px-3 rounded-xl bg-background-50/80" style={{ animationDelay: `${i * 200}ms` }}>
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-primary-700">{c.student.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground-900 truncate">{c.student}</p>
                          <p className="text-[11px] text-foreground-400">{c.grade}</p>
                        </div>
                        <div className="text-right">
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.statusColor}`}>{c.status}</span>
                          <p className="text-[11px] text-foreground-400 mt-0.5">{c.days}d desde última acción</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Alerts section */}
                <div className="widget-card mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-notification-3-line text-accent-500 text-sm" />
                    <span className="text-xs font-bold text-foreground-800 uppercase tracking-wider">Alertas activas</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-xs font-bold">{ALERTS.length}</span>
                  </div>
                  <div className="space-y-2">
                    {ALERTS.map((alert, i) => (
                      <div key={i} className="flex items-center gap-2.5 py-1.5 px-3 rounded-xl bg-background-50/80" style={{ animationDelay: `${i * 200}ms` }}>
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${alert.severity === "critical" ? "bg-red-500 animate-alert-blink" : "bg-amber-500"}`} />
                        <i className={`${alert.icon} text-xs ${alert.severity === "critical" ? "text-red-500" : "text-amber-500"}`} />
                        <span className="text-xs text-foreground-600 flex-1">{alert.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* AI Summary */}
                <div className="widget-card bg-gradient-to-br from-primary-50/80 to-white">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                      <i className="ri-brain-line text-white text-xs" />
                    </div>
                    <span className="text-xs font-bold text-foreground-800">Copiloto IA</span>
                    <span className="ml-auto text-xs text-foreground-400">Hoy, 09:42</span>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed">
                    La tasa de resolución de casos mejoró <strong className="text-foreground-800">+7%</strong> este mes. Se recomienda atender{" "}
                    <strong className="text-accent-600">3 casos críticos</strong> sin intervención. El cumplimiento de protocolos está dentro de los estándares institucionales.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-scroll-indicator">
        <a href="#convivencia-escolar" className="flex flex-col items-center gap-1.5 text-foreground-300 hover:text-foreground-600 transition-colors cursor-pointer">
          <span className="text-xs font-medium tracking-widest uppercase">Descubre más</span>
          <i className="ri-arrow-down-line text-base" />
        </a>
      </div>

      {/* Demo Modal */}
      {demoOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDemoOpen(false)} />
          <div className="relative bg-white rounded-3xl p-6 md:p-8 w-full max-w-md shadow-soft-lg animate-scale-in">
            <button onClick={() => setDemoOpen(false)} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 hover:text-foreground-700 hover:bg-background-200 transition-all cursor-pointer">
              <i className="ri-close-line" />
            </button>

            {formState === "success" ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-2xl bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-secondary-600 text-2xl" />
                </div>
                <h3 className="text-xl font-bold text-foreground-950 mb-2">¡Solicitud enviada!</h3>
                <p className="text-sm text-foreground-500">Nos pondremos en contacto contigo en las próximas 24 horas.</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-foreground-950 mb-1">Solicitar demo</h3>
                  <p className="text-sm text-foreground-500">Completa el formulario y te mostraremos cómo gestionar la convivencia escolar con inteligencia artificial.</p>
                </div>
                <form data-readdy-form onSubmit={handleDemoSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Nombre completo</label>
                    <input name="name" type="text" required className="input-modern px-4 py-3" placeholder="Ej: María González" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Correo electrónico</label>
                    <input name="email" type="email" required className="input-modern px-4 py-3" placeholder="maria@colegio.cl" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Institución</label>
                    <input name="institucion" type="text" required className="input-modern px-4 py-3" placeholder="Nombre del colegio" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Cargo</label>
                    <select name="cargo" required className="input-modern px-4 py-3 appearance-none">
                      <option value="">Selecciona tu cargo</option>
                      <option value="director">Director/a</option>
                      <option value="convivencia">Encargado/a de Convivencia</option>
                      <option value="orientador">Orientador/a</option>
                      <option value="psicologo">Psicólogo/a</option>
                      <option value="inspector">Inspector/a General</option>
                      <option value="sostenedor">Sostenedor/a</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Mensaje (opcional)</label>
                    <textarea name="mensaje" maxLength={500} rows={3} className="input-modern px-4 py-3 resize-none" placeholder="Cuéntanos qué necesitas gestionar..." />
                  </div>
                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="w-full px-6 py-3.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white text-sm font-bold rounded-2xl hover:from-primary-700 hover:to-primary-600 active:scale-[0.98] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {formState === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar solicitud
                        <i className="ri-send-plane-line" />
                      </>
                    )}
                  </button>
                  {formState === "error" && (
                    <p className="text-xs text-red-500 text-center">Ocurrió un error. Intenta nuevamente.</p>
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}